import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t.chat.greeting }]);
    }
  }, [open, messages.length, t.chat.greeting]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg, lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => m.map((it, i) => (i === m.length - 1 ? { ...it, text: snapshot } : it)));
      }
    } catch {
      setMessages((m) => m.map((it, i) => (i === m.length - 1 ? { ...it, text: t.chat.error } : it)));
    }
    setBusy(false);
  };

  return (
    <>
      <motion.button
        data-testid="chat-launcher-button"
        aria-label={t.chat.title}
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 3.6, duration: 0.55, type: "spring", bounce: 0.5 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 left-6 z-50 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#0D1B2A] text-[#F2A33C] shadow-2xl shadow-[#0D1B2A]/40"
      >
        {open ? <X size={24} /> : <Bot size={26} strokeWidth={2.2} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-4 z-50 flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-[#E6DEC8] bg-white shadow-2xl sm:left-6"
          >
            <div className="flex items-center gap-3 bg-[#0D1B2A] px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F2A33C]/15 text-[#F2A33C]">
                <Bot size={20} />
              </span>
              <div className="flex-1 leading-tight">
                <p className="font-serif text-base font-bold text-white">{t.chat.title}</p>
                <p className="text-xs text-white/60">{t.chat.subtitle}</p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                className="text-white/60 transition-colors hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#FAF6F0] px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-br-md bg-[#1B6B93] text-white"
                      : "mr-auto rounded-bl-md bg-white text-[#0D1B2A] shadow-sm"
                  }`}
                >
                  {m.text || (
                    <span className="inline-flex gap-1 py-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#0D1B2A]/40" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#0D1B2A]/40 [animation-delay:0.15s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#0D1B2A]/40 [animation-delay:0.3s]" />
                    </span>
                  )}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {t.chat.chips.map((chip, i) => (
                    <button
                      key={chip}
                      data-testid={`chat-chip-${i + 1}`}
                      onClick={() => send(chip)}
                      className="rounded-full border border-[#1B6B93]/30 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#1B6B93] transition-colors hover:bg-[#1B6B93] hover:text-white"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-[#E6DEC8] bg-white p-3">
              <a
                data-testid="chat-whatsapp-cta"
                href={waLink(t.waGeneric)}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-2.5 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-bold text-white transition-transform hover:scale-[1.02]"
              >
                <MessageCircle size={14} />
                {t.chat.whatsapp}
              </a>
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t.chat.placeholder}
                  className="flex-1 rounded-full border border-[#E6DEC8] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#1B6B93]"
                />
                <button
                  data-testid="chat-send-button"
                  type="submit"
                  disabled={busy || !input.trim()}
                  aria-label="Enviar"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0D1B2A] text-white transition-all hover:bg-[#1B6B93] disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
